import { useCallback, useEffect, useRef, useState } from 'react';

interface UseCountdownArgs {
  initialCount?: number;
  running?: boolean;
}

export const useCountdown = (
  onFinished: () => void,
  { initialCount = 5, running = true }: UseCountdownArgs = {}
) => {
  const [countdown, setCountdown] = useState(initialCount);
  const ref = useRef<NodeJS.Timeout>();

  // Avoid issues with the callback changing
  const finishedRef = useRef(onFinished);
  useEffect(() => {
    finishedRef.current = onFinished;
  }, [onFinished]);

  const stop = useCallback(() => {
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = undefined;
    }
  }, []);

  const start = useCallback(() => {
    setCountdown(initialCount);
    stop();
    ref.current = setInterval(() => setCountdown(curr => Math.max(curr - 1, 0)), 1000);
  }, [initialCount, stop]);

  useEffect(() => {
    if (running) {
      start();
    } else {
      stop();
    }
    return stop;
  }, [running, stop, start]);

  useEffect(() => {
    if (countdown === 0) {
      finishedRef.current();
    }
  }, [countdown]);

  return countdown;
};
